import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Cloud, CheckCircle2, Link2Off, Plug, Settings2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ErrorState, GuideBanner } from "@/components/PageState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OrgEditDialog } from "@/components/OrgEditDialog";
import { SYSTEM_LABELS, orgSummary } from "@/lib/connectionConfig";
import { useAuth } from "@/context/AuthContext";

export default function Connections() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [params, setParams] = useSearchParams();
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogSystem, setDialogSystem] = useState(null);
  const [sfOrgs, setSfOrgs] = useState({ active_id: "", orgs: [] });

  const load = () => api.get("/connections").then((r) => setConns(r.data)).catch(() => setConns(false));
  const loadSfOrgs = async () => {
    try {
      const { data } = await api.get("/salesforce/orgs");
      setSfOrgs(data);
      // Proactively health-check OAuth orgs so revoked tokens surface a reconnect banner.
      data.orgs.filter((o) => o.method === "oauth").forEach(async (o) => {
        try {
          const { data: h } = await api.get(`/salesforce/orgs/${o.id}/health`);
          setSfOrgs((prev) => ({
            ...prev,
            orgs: prev.orgs.map((x) => (x.id === o.id ? { ...x, auth_error: !!h.reconnect_required } : x)),
          }));
        } catch { /* ignore */ }
      });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    loadSfOrgs();
  }, []);

  useEffect(() => {
    const sf = params.get("salesforce");
    if (!sf) return;
    if (sf === "connected") {
      toast.success("Salesforce connected. Next: set a governance rule so I can watch limits.", {
        action: { label: "Set a rule", onClick: () => navigate("/dashboard/governance") },
      });
      window.dispatchEvent(new Event("gx-guide-refresh"));
    }
    else if (sf === "norefresh") toast.error("No refresh token returned — enable the refresh_token/offline_access scopes on your Connected App.");
    else if (sf === "error") toast.error("Salesforce authorization failed. Please try again.");
    params.delete("salesforce");
    setParams(params, { replace: true });
    load();
    loadSfOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activateOrg = async (id) => {
    try {
      await api.post(`/salesforce/orgs/${id}/activate`);
      setSfOrgs((p) => ({ ...p, active_id: id }));
      toast.success("Active org switched");
    } catch { toast.error("Couldn't switch org"); }
  };
  const removeSfOrg = async (id) => {
    try {
      await api.delete(`/salesforce/orgs/${id}`);
      toast.success("Org removed");
      loadSfOrgs();
      load();
    } catch { toast.error("Failed to remove org"); }
  };

  const reconnectOrg = async (id) => {
    try {
      toast.loading("Redirecting to Salesforce…", { id: "sf-reconnect" });
      const { data } = await api.post("/connections/salesforce/oauth/start", { reconnect_org_id: id });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.dismiss("sf-reconnect");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start reconnect");
    }
  };

  const removeOrg = async (system) => {
    setBusy(true);
    try {
      await api.delete(`/connections/${system}/org`);
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error("Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (conns === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (conns === false) {
    return (
      <ErrorState
        title="Connections unavailable"
        description="We couldn't load your systems. Try again — your orgs are still saved."
        onRetry={() => { setConns(null); load(); loadSfOrgs(); }}
        testid="connections-error"
      />
    );
  }

  return (
    <div data-testid="connections-page">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Connections</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Connected systems</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Start with Salesforce for live limits. Zoho and Oracle workspaces can be saved now; their meters stay sample until live APIs ship.
        </p>
      </div>

      <GuideBanner testid="connections-guide">
        Connect Salesforce first — extraction, governance alerts and Overview all use the <strong>active org</strong>.
      </GuideBanner>

      <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="salesforce-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Cloud className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-base font-bold tracking-tight">Salesforce orgs</p>
              <p className="text-xs text-muted-foreground">
                Connect several orgs — the active one is used for extraction, governance & the dashboard.
              </p>
            </div>
          </div>
          <Button className="gap-2 font-semibold" onClick={() => setDialogSystem("salesforce")} disabled={restricted} data-testid="add-org-salesforce">
            <Plug className="h-4 w-4" /> Add org
          </Button>
        </div>

        <div className="mt-4 space-y-2">
          {sfOrgs.orgs.length === 0 && (
            <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              No Salesforce orgs yet. Add one with OAuth 2.0 or username + password.
            </p>
          )}
          {sfOrgs.orgs.map((o) => {
            const isActive = o.id === sfOrgs.active_id;
            return (
              <div key={o.id} className="rounded-xl border border-border bg-background p-4" data-testid={`sf-org-${o.id}`}>
                {o.auth_error && (
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2" data-testid={`sf-reconnect-banner-${o.id}`}>
                    <span className="flex items-center gap-2 text-xs font-medium text-destructive">
                      <AlertTriangle className="h-4 w-4" /> This org's authorization expired or was revoked.
                    </span>
                    <Button size="sm" variant="destructive" className="h-7" onClick={() => reconnectOrg(o.id)} disabled={restricted} data-testid={`sf-reconnect-${o.id}`}>
                      Reconnect
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium">{o.label}</p>
                      {isActive && <Badge className="gap-1" data-testid={`sf-org-active-${o.id}`}><CheckCircle2 className="h-3 w-3" /> Active</Badge>}
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">{o.method}</span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {o.environment}{o.username ? ` · ${o.username}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {!isActive && (
                      <Button variant="outline" size="sm" onClick={() => activateOrg(o.id)} disabled={restricted} data-testid={`sf-activate-${o.id}`}>
                        Make active
                      </Button>
                    )}
                    {o.method === "oauth" && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => reconnectOrg(o.id)} disabled={restricted} data-testid={`sf-reconnect-row-${o.id}`}>
                        <RefreshCw className="h-3.5 w-3.5" /> Reconnect
                      </Button>
                    )}
                    <Button variant="outline" size="icon" className="h-9 w-9 text-destructive hover:text-destructive" onClick={() => removeSfOrg(o.id)} disabled={restricted} data-testid={`sf-remove-${o.id}`} aria-label="Remove org">
                      <Link2Off className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {conns.filter((c) => c.system !== "salesforce").map((c) => {
          const connected = c.connected;
          const isOAuth = c.method === "oauth";
          return (
            <div
              key={c.system}
              className="flex flex-col rounded-2xl border border-border bg-card p-5"
              data-testid={`connection-card-${c.system}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${connected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                    <Cloud className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-display text-base font-bold tracking-tight">
                      {SYSTEM_LABELS[c.system] || c.system_name}
                    </p>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                      {c.system.startsWith("zoho") ? "Workspace" : isOAuth ? "OAuth" : "Credentials"}
                    </span>
                  </div>
                </div>
                <Badge variant={connected ? "default" : "secondary"} className="gap-1" data-testid={`connection-status-${c.system}`}>
                  {connected ? <CheckCircle2 className="h-3 w-3" /> : <Link2Off className="h-3 w-3" />}
                  {connected ? "Connected" : "Disconnected"}
                </Badge>
              </div>

              {connected && c.org && (
                <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
                  <p className="truncate font-medium text-foreground">{c.org.label}</p>
                  <p className="truncate">{orgSummary(c.org)}</p>
                </div>
              )}

              <div className="mt-5">
                {connected ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 gap-2"
                      onClick={() => navigate(`/dashboard/connections/${c.system}`)}
                      data-testid={`manage-org-${c.system}`}
                    >
                      <Settings2 className="h-4 w-4" /> Manage
                    </Button>
                    <Button variant="outline" size="icon" className="h-10 w-10 shrink-0 text-destructive hover:text-destructive" onClick={() => removeOrg(c.system)} disabled={busy || restricted} data-testid={`remove-org-${c.system}`} aria-label="Disconnect">
                      <Link2Off className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button className="w-full gap-2 font-semibold" onClick={() => setDialogSystem(c.system)} disabled={restricted} data-testid={`add-org-${c.system}`}>
                    <Plug className="h-4 w-4" /> {c.system.startsWith("zoho") ? "Add workspace" : "Add org"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <OrgEditDialog
        system={dialogSystem}
        open={!!dialogSystem}
        onOpenChange={(o) => !o && setDialogSystem(null)}
        mode="add"
        onSaved={() => { load(); loadSfOrgs(); }}
      />
    </div>
  );
}
